import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router';
import { Route, Switch } from 'react-router-dom';

import { GiftIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	CardList,
	Container,
	FlexGap,
	Jumbo,
	PAGE_CONTENT_WIDTH,
	Subheading,
	tokens,
} from '@treams/treams-ui';

import { ComplimentCreateModalWithID } from '../packages/kakapo-ui-application/pages/compliments/ComplimentCreateModal';
import ComplimentCreateThankYou from '../packages/kakapo-ui-application/pages/compliments/ComplimentThankYou';
import IncomingComplimentCard from './IncomingComplimentCard';
import MutualComplimentsList from './MutualComplimentsList';
import OutgoingComplimentCard from './OutgoingComplimentCard';
import UserFeedbackButtons from './UserFeedbackButtons';
import UserFeedbackTabs from './UserFeedbackTabs';

export default function UserCompliments() {
	const { t } = useTranslation();

	const { params } = useRouteMatch<{ id: string }>();
	const userID = params.id;

	const userQuery = graphql.useUserQuery({ variables: { id: userID } });

	const query = graphql.useUserComplimentsQuery({
		variables: { last: 100, id: userID },
	});
	const givenQuery = graphql.useUserGivenComplimentsQuery({
		variables: { last: 100, id: userID },
	});
	const viewerGivenQuery = graphql.useViewerGivenComplimentsQuery({
		variables: { last: 100, id: userID },
	});

	return (
		<>
			<Jumbo
				title={
					userQuery.data?.user.viewerCanViewFeedback
						? t('user.feedback.full.compliments.title', {
								firstName: userQuery.data?.user.firstName,
							})
						: t('user.feedback.compliments.title', { name: userQuery.data?.user.firstName })
				}
				button={<UserFeedbackButtons user={userQuery.data?.user as graphql.User} />}
				alignItems="flex-start"
				footer={<UserFeedbackTabs user={userQuery.data?.user as graphql.User} />}
			/>

			{userQuery.data?.user.viewerCanViewFeedback ? (
				<Container mt={4} mb={4} wide={PAGE_CONTENT_WIDTH}>
					<FlexGap flexDirection="column" gap={tokens.gap.page}>
						<FlexGap flexDirection="column" gap={tokens.gap.section}>
							<Subheading>{t('feedback.compliments.section.incoming')}</Subheading>
							<CardList
								translationName="compliments.incoming"
								query={query}
								path="user.compliments"
								icon={<GiftIcon />}
								compact
								renderCard={(item: graphql.Compliment) => (
									<IncomingComplimentCard key={item.id} userID={userID} compliment={item} />
								)}
								renderSkeleton={() => <IncomingComplimentCard.Skeleton />}
							/>
						</FlexGap>

						<FlexGap flexDirection="column" gap={tokens.gap.section}>
							<Subheading>{t('feedback.compliments.section.outgoing')}</Subheading>
							<CardList
								translationName="compliments.outgoing"
								query={givenQuery}
								path="user.givenCompliments"
								icon={<GiftIcon />}
								compact
								renderCard={(item: graphql.Compliment) => (
									<OutgoingComplimentCard key={item.id} compliment={item} />
								)}
								renderSkeleton={() => <OutgoingComplimentCard.Skeleton />}
							/>
						</FlexGap>
					</FlexGap>
				</Container>
			) : (
				<MutualComplimentsList query={viewerGivenQuery} userID={userID} />
			)}

			<Switch>
				<Route path="/users/:id/feedback/compliments/new">
					{({ match }) => <ComplimentCreateModalWithID userID={match!.params.id!} />}
				</Route>
				<Route path="/users/:id/feedback/compliments/thank-you">
					{({ match }) => <ComplimentCreateThankYou userID={match!.params.id!} />}
				</Route>
			</Switch>
		</>
	);
}
